import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import urlParse from 'url-parse';
import { format, subDays, parseISO } from 'date-fns';
import {
  loadCreateIfPageData,
  selectCreateIfPageData,
} from '@client/pages/createIFPage/duckCreateIfPage';
import { remapObjValueToString } from '@client/shared/utils/utils/utils';
import FormWrapper from '@client/components/formComponents/formWrapper/FormWrapper';
import { getIn } from '@client/shared/utils/immutableUtils/immutableUtils';
import { selectAccessToken } from '@client/redux/user';
import { selectUserInfo } from '@client/redux/user/userReducer';
import { postFundraiser } from '@client/connectivity/api.fundraiser';
import { createFundraiserSchema } from '@client/shared/utils/validation/validation';
import { NotificationContext } from '@client/components/notificationProvider/NotificationProvider';
import { selectActiveEvents } from '@client/redux/event/eventReducer';
import { selectFundraiserTypes } from '@client/redux/fundraiser/fundraiserSelectors';
import FormCreateFundraiser from '@client/pages/createIFPage/formCreateFundraiser/FormCreateFundraiser';
import { trackCustomEvent } from '@client/tracking/GTM';
import { setFundraiserDraftDate } from '@client/helpers/draftFundraisers';
import {
  EVENT_GA,
  CATEGORY_EGEN_INSAMLING,
  ACTION_FUNDRAISER_STARTED,
} from '@client/definitions/tracking';
import styles from './createIFPage.scss';

class CreateIFPage extends PureComponent {
  static contextType = NotificationContext;
  static loadData(dispatch) {
    return dispatch(loadCreateIfPageData());
  }

  trackStartedFundraiser(slug) {
    trackCustomEvent(EVENT_GA, {
      form_title: CATEGORY_EGEN_INSAMLING,
      form_interaction: ACTION_FUNDRAISER_STARTED,
      click_text: slug,
    });
  }

  componentDidMount() {
    CreateIFPage.loadData(this.props.dispatch);
  }

  handleSubmit = (values, { setErrors }) => {
    const { accessToken, history } = this.props;
    values.fundraiserType = parseInt(values.fundraiserType);

    // Normalize and validate memorial dates on submit
    const toIsoDate = (str) => {
      if (typeof str !== 'string') return str;
      const trimmed = str.trim();
      // Insert dashes if user entered YYYYMMDD
      const eightDigits = trimmed.match(/^(\d{4})(\d{2})(\d{2})$/);
      if (eightDigits) {
        return `${eightDigits[1]}-${eightDigits[2]}-${eightDigits[3]}`;
      }
      return trimmed;
    };

    const isMemorial = values.fundraiserType === 2;
    if (isMemorial) {
      const dateRegex = /^(?:[12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/;
      const errors = {};
      const normalizedBirth = toIsoDate(values.memorialBirthDate);
      const normalizedDeath = toIsoDate(values.memorialDeathDate);

      if (!dateRegex.test(normalizedBirth)) {
        errors.memorialBirthDate = 'Ange datum som YYYY-MM-DD.';
      }
      if (!dateRegex.test(normalizedDeath)) {
        errors.memorialDeathDate = 'Ange datum som YYYY-MM-DD.';
      }
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        return;
      }

      values.memorialBirthDate = normalizedBirth;
      values.memorialDeathDate = normalizedDeath;
    }

    // Set the fundraisers initial state to draft.
    values.startDate = setFundraiserDraftDate();
    values.endDate = setFundraiserDraftDate();

    postFundraiser(values, accessToken)
      .then((res) => {
        history.push(`/insamling/${res.data.slug}?edit=true&new=true`);
        this.trackStartedFundraiser(res?.data?.slug);
      })
      .catch((error) => {
        console.log('error: ', error);
        if (error) {
          if (error.status) {
            const errors = error.errors;

            if (errors) {
              const mappedErrors = remapObjValueToString(errors);

              return setErrors(mappedErrors);
            } else {
              this.context.displayNotification(
                'ERROR',
                error.title || error.detail || 'Ett fel inträffade'
              );
            }
          }
        }
      });
  };

  renderForm = (formProps) => {
    const {
      pageData,
      events = [],
      fundraiserTypes = [],
      userInfo,
      location,
    } = this.props;

    return (
      <FormCreateFundraiser
        formProps={formProps}
        translations={pageData}
        userInfo={userInfo}
        location={location}
        pageData={pageData}
        events={events}
        fundraiserTypes={fundraiserTypes}
      />
    );
  };

  render() {
    const { userInfo, location } = this.props;
    const urlQueryParams = urlParse(location.search, true);

    return (
      <div className={`${styles.createFundraiserPage}`}>
        <FormWrapper
          initialValues={{
            firstName: getIn(userInfo, 'given_name'),
            lastName: getIn(userInfo, 'family_name'),
            email: getIn(userInfo, 'email'),
            fundraiserType: urlQueryParams.query.type || '1',
            eventName: urlQueryParams.query.eventName,
            startDate: new Date(),
          }}
          onSubmit={this.handleSubmit}
          component={this.renderForm}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={createFundraiserSchema()}
        />
      </div>
    );
  }
}

CreateIFPage.propTypes = {
  dispatch: PropTypes.func,
  accessToken: PropTypes.string,
  pageData: ImmutablePropTypes.map,
  events: ImmutablePropTypes.list,
  fundraiserTypes: ImmutablePropTypes.list,
  userInfo: ImmutablePropTypes.map,
  location: PropTypes.object,
  history: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    pageData: selectCreateIfPageData(state),
    events: selectActiveEvents(state),
    fundraiserTypes: selectFundraiserTypes(state),
    userInfo: selectUserInfo(state),
    accessToken: selectAccessToken(state),
  };
}

export default connect(mapStateToProps)(CreateIFPage);
